import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import Modal from "../../../components/base/Modal";
import QRGenerator, { toPngDataUrl, toSvg } from "../../../components/base/QRGenerator";
import { buildThemeClasses } from "../theme";
import { trackShortLinkCreated, trackShortLinkQrDownloaded } from "../../../analytics/crmAnalytics";
import {
  CHANNELS,
  CHANNEL_LABEL,
  PRESETS,
  SHORT_LINK_PRODUCTS,
  createShortLink,
  defaultUtm,
  fetchShortLinkStats,
  fetchShortLinks,
  formatLinkDate,
  isCanceled,
  presetsForProduct,
  previewTargetUrl,
  shortLinkErrorMessage,
  shortUrl,
  SHORT_LINK_DISPLAY,
  updateShortLink,
  type Channel,
  type Preset,
  type ShortLink,
  type ShortLinkStats,
  type Utm,
} from "../services/shortLinksService";

// ===========================================================================
// Marketing → Enlaces cortos y QR (épica #1518, issue #1522). LIVE.
//
// Un enlace `<base del tenant>/r/<code>` por cada lugar donde se
// promociona WAYAK (folleto, anuncio, WhatsApp, punto de venta…). Al visitarlo,
// la API cuenta el clic y manda al destino con `utm_*`, `ref=<code>` y
// `channel`: así el lead y el negocio saben de dónde vinieron.
//
// Aquí se crean (con las cinco opciones del funnel de WAYAK o la app de Movik
// como atajos, según el producto elegido, #1536), se copian, se
// descargan como QR (SVG para imprenta, PNG para pantalla) y se ven sus clics.
// ===========================================================================

const UTM_FIELDS: { key: keyof Utm; label: string; hint: string }[] = [
  { key: "source", label: "utm_source", hint: "De dónde viene: whatsapp, expo, folleto…" },
  { key: "medium", label: "utm_medium", hint: "qr, link, cpc, chatbot…" },
  { key: "campaign", label: "utm_campaign", hint: "Nombre de la campaña o de la pieza" },
  { key: "content", label: "utm_content", hint: "Variante (opcional)" },
  { key: "term", label: "utm_term", hint: "Palabra clave (opcional)" },
];

const CHANNEL_TONE: Record<Channel, string> = {
  landing: "bg-sky-500/15 text-sky-600",
  whatsapp: "bg-emerald-500/15 text-emerald-600",
  meta_ads: "bg-indigo-500/15 text-indigo-500",
  qr: "bg-violet-500/15 text-violet-600",
  pos: "bg-amber-500/15 text-amber-600",
  flyer: "bg-orange-500/15 text-orange-600",
  app_store: "bg-pink-500/15 text-pink-600",
  referral: "bg-teal-500/15 text-teal-600",
  other: "bg-gray-400/15 text-gray-500",
};

interface Draft {
  presetId: Preset["id"] | "custom";
  channel: Channel;
  label: string;
  product: string;
  target: string;
  utm: Utm;
}

const PRESET_BY_ID = new Map(PRESETS.map((p) => [p.id, p]));

/** El borrador inicial para una opción y un canal (etiqueta, destino y utm sugeridos). */
export function draftFor(presetId: Draft["presetId"], channel: Channel, previous?: Partial<Draft>): Draft {
  const preset = presetId === "custom" ? null : PRESET_BY_ID.get(presetId) ?? null;
  return {
    presetId,
    channel,
    label: preset ? `${preset.label} · ${CHANNEL_LABEL[channel]}` : previous?.label ?? "",
    product: preset ? preset.product : previous?.product ?? "wayak",
    target: preset ? preset.target : previous?.target ?? "",
    utm: defaultUtm(channel, preset ? preset.id : null),
  };
}

/**
 * Cambiar de producto (#1536): se toma su primer atajo; si no tiene, queda
 * «Otro destino» con el destino vacío (el de otro producto no le sirve).
 */
export function draftForProduct(product: string, current: Draft): Draft {
  const first = presetsForProduct(product)[0];
  if (first) return { ...draftFor(first.id, current.channel, current), product };
  return { ...draftFor("custom", current.channel, current), product, target: "" };
}

/**
 * Copia al portapapeles. `navigator.clipboard` sólo existe en contextos seguros
 * y a veces el navegador lo niega; entonces se recurre a un <textarea> temporal
 * + `document.execCommand("copy")` (obsoleto, pero es el único plan B).
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // se intenta el plan B
  }
  const area = document.createElement("textarea");
  try {
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    area.remove();
  }
}

/** Descarga un archivo desde el navegador (SVG como texto, PNG como data URL). */
function download(filename: string, href: string) {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function EnlacesSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [links, setLinks] = useState<ShortLink[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftFor("demo", "qr"));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [statsFor, setStatsFor] = useState<ShortLink | null>(null);
  const [stats, setStats] = useState<ShortLinkStats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [busyCode, setBusyCode] = useState<string | null>(null);

  const tx = (key: string, fallback: string, opts?: Record<string, unknown>) => t(`crm.shortLinks.${key}`, fallback, opts) as string;

  useEffect(() => {
    const controller = new AbortController();
    fetchShortLinks(controller.signal)
      .then((list) => {
        setLinks(list);
        setLoadError(null);
      })
      .catch((err) => {
        if (!isCanceled(err)) setLoadError(shortLinkErrorMessage(err, tx("loadError", "No se pudieron leer los enlaces.")));
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!statsFor) return;
    const controller = new AbortController();
    setStats(null);
    setStatsError(null);
    fetchShortLinkStats(statsFor.code, 30, controller.signal)
      .then(setStats)
      .catch((err) => {
        if (!isCanceled(err)) setStatsError(shortLinkErrorMessage(err, tx("statsError", "No se pudieron leer las estadísticas.")));
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsFor]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!links) return [];
    if (!q) return links;
    return links.filter((l) => [l.label, l.code, l.target, l.product ?? "", CHANNEL_LABEL[l.channel] ?? ""].some((v) => v.toLowerCase().includes(q)));
  }, [links, search]);

  const totalClicks = useMemo(() => (links ?? []).reduce((acc, l) => acc + (l.clicks || 0), 0), [links]);

  const flash = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice((current) => (current === message ? null : current)), 3000);
  };

  const copy = async (text: string) => {
    if (await copyToClipboard(text)) flash(tx("copied", "Enlace copiado: {{url}}", { url: text }));
    else flash(tx("copyFailed", "No se pudo copiar; selecciona el enlace y cópialo a mano."));
  };

  const downloadSvg = async (link: ShortLink) => {
    const svg = await toSvg(shortUrl(link.code), { size: 1024 });
    download(`qr-${link.code}.svg`, `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
    trackShortLinkQrDownloaded("svg", link);
  };

  const downloadPng = async (link: ShortLink) => {
    download(`qr-${link.code}.png`, await toPngDataUrl(shortUrl(link.code), { size: 1024 }));
    trackShortLinkQrDownloaded("png", link);
  };

  const toggleActive = async (link: ShortLink) => {
    setBusyCode(link.code);
    try {
      const updated = await updateShortLink(link.code, { active: !link.active });
      setLinks((current) => (current ?? []).map((l) => (l.code === link.code ? { ...l, ...updated } : l)));
    } catch (err) {
      flash(shortLinkErrorMessage(err, tx("saveError", "No se pudo guardar el enlace.")));
    } finally {
      setBusyCode(null);
    }
  };

  const openCreate = () => {
    setDraft(draftFor("demo", "qr"));
    setSaveError(null);
    setCreating(true);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const created = await createShortLink({
        label: draft.label.trim(),
        channel: draft.channel,
        target: draft.target.trim(),
        utm: draft.utm,
        product: draft.product.trim() || null,
      });
      setLinks((current) => [created, ...(current ?? [])]);
      setCreating(false);
      trackShortLinkCreated(created);
      flash(tx("created", "Enlace creado: {{url}}", { url: shortUrl(created.code) }));
    } catch (err) {
      setSaveError(shortLinkErrorMessage(err, tx("saveError", "No se pudo guardar el enlace.")));
    } finally {
      setSaving(false);
    }
  };

  const setUtm = (key: keyof Utm, value: string) => setDraft((d) => ({ ...d, utm: { ...d.utm, [key]: value } }));
  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-primary-500 ${tc.border} ${tc.cardBg} ${tc.text}`;
  const labelClass = `mb-1 block text-xs font-medium ${tc.textSecondary}`;
  const maxDay = Math.max(1, ...(stats?.byDay.map((d) => d.clicks) ?? [1]));

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-qr-code-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>{tx("title", "Enlaces cortos y QR")}</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                {tx("subtitle", "Un enlace por cada lugar donde promocionas: cuenta las visitas y marca de dónde vino cada lead.")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={tx("search", "Buscar enlace…")}
                aria-label={tx("search", "Buscar enlace…")}
                className={`bg-transparent text-sm outline-none ${tc.text}`}
              />
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-400"
            >
              <i className="ri-add-line" />
              <span className="whitespace-nowrap">{tx("new", "Nuevo enlace")}</span>
            </button>
          </div>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label={tx("statLinks", "Enlaces")} value={String(links?.length ?? "—")} hint={tx("statLinksHint", "Activos e inactivos")} tc={tc} />
          <Stat label={tx("statClicks", "Clics totales")} value={links ? String(totalClicks) : "—"} hint={tx("statClicksHint", "Todas las visitas registradas")} tc={tc} />
          <Stat label={tx("statActive", "Activos")} value={links ? String(links.filter((l) => l.active).length) : "—"} hint={tx("statActiveHint", "Un enlace apagado manda a la portada")} tc={tc} />
          <Stat label={tx("statBase", "Dominio")} value={SHORT_LINK_DISPLAY} hint={tx("statBaseHint", "Mismo dominio para todos los QR")} tc={tc} />
        </div>

        {loadError && (
          <p role="alert" className="mb-3 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500">
            <i className="ri-error-warning-line mr-1" />
            {loadError}
          </p>
        )}
        {notice && (
          <p role="status" className="mb-3 rounded-lg bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600">
            <i className="ri-checkbox-circle-line mr-1" />
            {notice}
          </p>
        )}

        <section aria-label={tx("listAria", "Enlaces cortos")} className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
          {!links && !loadError ? (
            <p className={`text-sm ${tc.textSecondary}`}>{tx("loading", "Cargando enlaces…")}</p>
          ) : visible.length === 0 ? (
            <p className={`text-sm ${tc.textSecondary}`}>
              {links && links.length === 0
                ? tx("empty", "Todavía no hay enlaces. Crea el primero con «Nuevo enlace»: elige la opción del funnel y el canal, y descarga el QR.")
                : tx("emptySearch", "Ningún enlace coincide con la búsqueda.")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`text-left text-xs ${tc.textSecondary}`}>
                    <th className="pb-2 pr-3 font-medium">{tx("colLabel", "Enlace")}</th>
                    <th className="pb-2 pr-3 font-medium">{tx("colChannel", "Canal")}</th>
                    <th className="pb-2 pr-3 font-medium">{tx("colTarget", "Destino")}</th>
                    <th className="pb-2 pr-3 text-right font-medium">{tx("colClicks", "Clics")}</th>
                    <th className="pb-2 pr-3 font-medium">{tx("colLastClick", "Último clic")}</th>
                    <th className="pb-2 pr-3 font-medium">{tx("colStatus", "Estado")}</th>
                    <th className="pb-2 text-center font-medium">{tx("colActions", "Acciones")}</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((l) => {
                    const url = shortUrl(l.code);
                    return (
                      <tr key={l.code} className={`border-t ${tc.border} ${tc.text}`}>
                        <td className="py-2 pr-3">
                          <div className="font-medium">{l.label}</div>
                          <button
                            type="button"
                            onClick={() => copy(url)}
                            title={tx("copy", "Copiar enlace")}
                            className="mt-0.5 flex items-center gap-1 font-mono text-[11px] text-primary-500 hover:underline"
                          >
                            <i className="ri-file-copy-line" />
                            {url}
                          </button>
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`inline-block whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium ${CHANNEL_TONE[l.channel] ?? CHANNEL_TONE.other}`}>
                            {CHANNEL_LABEL[l.channel] ?? l.channel}
                          </span>
                        </td>
                        <td className="max-w-[260px] py-2 pr-3">
                          <div className="truncate" title={previewTargetUrl(l.target, l.utm, l.code, l.channel)}>
                            {l.target}
                          </div>
                          {l.utm?.campaign && <div className={`text-[11px] ${tc.textSecondary}`}>utm_campaign={l.utm.campaign}</div>}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">{l.clicks}</td>
                        <td className={`whitespace-nowrap py-2 pr-3 text-xs ${tc.textSecondary}`}>{formatLinkDate(l.lastClickAt, true)}</td>
                        <td className="py-2 pr-3">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={l.active}
                            aria-label={tx("activeSwitch", "Activo: {{label}}", { label: l.label })}
                            disabled={busyCode === l.code}
                            onClick={() => toggleActive(l)}
                            className={`inline-block whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium disabled:opacity-40 ${l.active ? "bg-emerald-500/15 text-emerald-600" : "bg-gray-400/15 text-gray-500"}`}
                          >
                            {l.active ? tx("active", "Activo") : tx("inactive", "Apagado")}
                          </button>
                        </td>
                        <td className="py-2">
                          <div className="flex items-center justify-center gap-0.5">
                            <IconButton icon="ri-file-copy-line" title={tx("copy", "Copiar enlace")} onClick={() => copy(url)} tone={tc.buttonText} />
                            <IconButton icon="ri-qr-code-line" title={tx("qrSvg", "Descargar QR (SVG)")} onClick={() => void downloadSvg(l)} tone={tc.buttonText} />
                            <IconButton icon="ri-image-line" title={tx("qrPng", "Descargar QR (PNG)")} onClick={() => void downloadPng(l)} tone={tc.buttonText} />
                            <IconButton icon="ri-bar-chart-2-line" title={tx("stats", "Ver estadísticas")} onClick={() => setStatsFor(l)} tone="text-primary-500" />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {creating && (
        <Modal
          title={tx("newTitle", "Nuevo enlace corto")}
          onClose={() => setCreating(false)}
          themeClasses={tc}
          width="max-w-3xl"
          content={
            <form onSubmit={submit} className="space-y-4 p-4 sm:p-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="sl-preset" className={labelClass}>{tx("fieldPreset", "Opción del funnel")}</label>
                  <select
                    id="sl-preset"
                    value={draft.presetId}
                    onChange={(e) => setDraft((d) => draftFor(e.target.value as Draft["presetId"], d.channel, d))}
                    className={inputClass}
                  >
                    {presetsForProduct(draft.product).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label} — {p.description}
                      </option>
                    ))}
                    <option value="custom">{tx("presetCustom", "Otro destino (lo escribo yo)")}</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="sl-channel" className={labelClass}>{tx("fieldChannel", "Canal")}</label>
                  <select
                    id="sl-channel"
                    value={draft.channel}
                    onChange={(e) => setDraft((d) => draftFor(d.presetId, e.target.value as Channel, d))}
                    className={inputClass}
                  >
                    {CHANNELS.map((c) => (
                      <option key={c} value={c}>
                        {CHANNEL_LABEL[c]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="sl-label" className={labelClass}>{tx("fieldLabel", "Etiqueta")}</label>
                  <input id="sl-label" required maxLength={120} value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} className={inputClass} placeholder="QR folleto expo Guadalajara" />
                </div>
                <div>
                  <label htmlFor="sl-product" className={labelClass}>{tx("fieldProduct", "Producto")}</label>
                  <select id="sl-product" value={draft.product} onChange={(e) => setDraft((d) => draftForProduct(e.target.value, d))} className={inputClass}>
                    {SHORT_LINK_PRODUCTS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="sl-target" className={labelClass}>{tx("fieldTarget", "Destino (https, dominios permitidos de WAYAK / Movik)")}</label>
                  <input id="sl-target" required type="url" value={draft.target} onChange={(e) => setDraft((d) => ({ ...d, target: e.target.value }))} className={inputClass} placeholder="https://wayak-logistics.com/#contacto" />
                </div>
              </div>

              <fieldset>
                <legend className={`mb-2 text-xs font-semibold ${tc.textSecondary}`}>{tx("utmTitle", "Atribución (utm). Se anexa al destino junto con ref=<código> y channel.")}</legend>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {UTM_FIELDS.map((f) => (
                    <div key={f.key}>
                      <label htmlFor={`sl-utm-${f.key}`} className={labelClass}>{f.label}</label>
                      <input id={`sl-utm-${f.key}`} maxLength={200} value={draft.utm[f.key] ?? ""} onChange={(e) => setUtm(f.key, e.target.value)} className={inputClass} placeholder={f.hint} />
                    </div>
                  ))}
                </div>
              </fieldset>

              {draft.target && (
                <p className={`break-all rounded-lg px-3 py-2 font-mono text-[11px] ${tc.textSecondary} bg-surface`} data-testid="target-preview">
                  {previewTargetUrl(draft.target, draft.utm, "<código>", draft.channel)}
                </p>
              )}

              {saveError && (
                <p role="alert" className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500">
                  <i className="ri-error-warning-line mr-1" />
                  {saveError}
                </p>
              )}

              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setCreating(false)} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.text}`}>
                  {tx("cancel", "Cancelar")}
                </button>
                <button type="submit" disabled={saving} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:opacity-50">
                  {saving ? tx("saving", "Creando…") : tx("create", "Crear enlace")}
                </button>
              </div>
            </form>
          }
        />
      )}

      {statsFor && (
        <Modal
          title={`${tx("statsTitle", "Estadísticas")} — ${statsFor.label}`}
          onClose={() => setStatsFor(null)}
          themeClasses={tc}
          width="max-w-3xl"
          content={
            <div className="grid gap-6 p-4 sm:grid-cols-[auto_1fr] sm:p-6">
              <div className="flex flex-col items-center gap-2">
                <QRGenerator value={shortUrl(statsFor.code)} size={160} />
                <code className="font-mono text-[11px] text-primary-500">{shortUrl(statsFor.code)}</code>
                <div className="flex gap-1">
                  <IconButton icon="ri-file-copy-line" title={tx("copy", "Copiar enlace")} onClick={() => copy(shortUrl(statsFor.code))} tone={tc.buttonText} />
                  <IconButton icon="ri-qr-code-line" title={tx("qrSvg", "Descargar QR (SVG)")} onClick={() => void downloadSvg(statsFor)} tone={tc.buttonText} />
                  <IconButton icon="ri-image-line" title={tx("qrPng", "Descargar QR (PNG)")} onClick={() => void downloadPng(statsFor)} tone={tc.buttonText} />
                </div>
              </div>
              <div>
                <div className="mb-3 grid grid-cols-2 gap-3">
                  <Stat label={tx("statsTotal", "Clics (30 días)")} value={stats ? String(stats.total) : "…"} hint={tx("statsTotalHint", "Visitas al enlace")} tc={tc} />
                  <Stat label={tx("statsAll", "Clics acumulados")} value={String(statsFor.clicks)} hint={`${tx("statsLast", "Último")}: ${formatLinkDate(statsFor.lastClickAt, true)}`} tc={tc} />
                </div>
                {statsError && (
                  <p role="alert" className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500">{statsError}</p>
                )}
                {stats && (
                  <div aria-label={tx("statsChart", "Clics por día")} className="flex h-32 items-end gap-[2px]">
                    {stats.byDay.map((d) => (
                      <div
                        key={d.date}
                        title={`${d.date}: ${d.clicks}`}
                        className="flex-1 rounded-t bg-primary-500/70"
                        style={{ height: `${Math.max(2, Math.round((d.clicks / maxDay) * 100))}%` }}
                      />
                    ))}
                  </div>
                )}
                <p className={`mt-2 break-all text-[11px] ${tc.textSecondary}`}>
                  {tx("statsTargetLabel", "Destino final")}: {previewTargetUrl(statsFor.target, statsFor.utm, statsFor.code, statsFor.channel)}
                </p>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
}

function Stat({ label, value, hint, tc }: { label: string; value: string; hint: string; tc: ReturnType<typeof buildThemeClasses> }) {
  return (
    <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
      <p className={`text-xs ${tc.textSecondary}`}>{label}</p>
      <p className={`mt-1 text-lg font-bold tabular-nums ${tc.text}`}>{value}</p>
      <p className={`text-[11px] ${tc.textSecondary}`}>{hint}</p>
    </div>
  );
}

function IconButton({ icon, title, onClick, tone }: { icon: string; title: string; onClick: () => void; tone: string }) {
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} className={`rounded-md p-1.5 ${tone}`}>
      <i className={icon} />
    </button>
  );
}
